/*
 * Tyre Finder — filter top bar
 * ------------------------------------------------------------------
 * <tyre-finder> rendered by snippets/section-block-tyre-finder.liquid
 * with filter_mode: true, inside the filter top bar form.
 *
 * Never fetches. The dropdowns are rebuilt from the form's hidden filter
 * inputs (data-filter + data-count), so they always mirror the filters
 * available on the page. Choosing a value toggles the matching hidden
 * checkbox so the theme's own filter JS applies it, exactly like the
 * quick-filter tiles. Each <select class="finder-dropdown"> carries
 * data-param and data-placeholder.
 */
if (!customElements.get('tyre-finder')) {
  class TyreFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      const selects = [...this.querySelectorAll('select.finder-dropdown')];
      if (!selects.length) return;

      this.form = this.closest('form[data-filter-form]');
      if (!this.form) return;

      this.dataset.initialized = 'true';
      this.levels = selects.map((select) => ({
        select,
        param: select.dataset.param,
        placeholder: select.dataset.placeholder || 'Select',
      }));

      this.levels.forEach((level, index) => {
        level.select.addEventListener('change', (event) => this.onChange(index, event));
      });

      this.watchForm();
      this.syncFromForm();
    }

    onChange(index, event) {
      event.stopPropagation();
      const value = this.levels[index].select.value;

      // Clear the levels below; they refill once the theme re-renders the hidden inputs
      this.levels.slice(index + 1).forEach((level) => {
        this.fillItems(level.select, level.placeholder, []);
        level.select.disabled = true;
      });
      if (value && index + 1 < this.levels.length) this.setLoading(this.levels[index + 1].select, true);

      const wanted = {};
      this.levels.slice(0, index + 1).forEach((level) => {
        if (level.select.value) wanted[level.param] = level.select.value;
      });
      this.applyFilters(wanted);
    }

    applyFilters(wanted) {
      const params = this.levels.map((l) => l.param);

      this.form.querySelectorAll('input[type="checkbox"][data-filter-item-input][data-filter]').forEach((input) => {
        const filter = input.getAttribute('data-filter') || '';
        const param = params.find((p) => filter.startsWith(`${p}=`));
        if (!param) return;

        const shouldBeChecked = wanted[param] !== undefined && filter === `${param}=${wanted[param]}`;
        if (input.checked !== shouldBeChecked) {
          input.checked = shouldBeChecked;
          this.selfWrite = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          this.selfWrite = false;
        }
      });
    }

    readForm() {
      const result = this.levels.map(() => ({ items: [], active: '' }));
      const seen = new Set();

      this.form.querySelectorAll('input[type="checkbox"][data-filter-item-input][data-filter]').forEach((input) => {
        const filter = input.getAttribute('data-filter') || '';
        const index = this.levels.findIndex((l) => filter.startsWith(`${l.param}=`));
        if (index < 0 || seen.has(filter)) return;
        seen.add(filter);

        const value = filter.slice(this.levels[index].param.length + 1);
        const count = parseInt(input.getAttribute('data-count') || '0', 10);
        const label = input.getAttribute('data-label') || value;

        if (input.checked) result[index].active = value;
        if (input.checked || count > 0) result[index].items.push({ value, label });
      });

      const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true });
      result.forEach((r) => r.items.sort(byLabel));
      return result;
    }

    syncFromForm() {
      const state = this.readForm();

      this.levels.forEach((level, index) => {
        const { items, active } = state[index];
        const previousActive = index === 0 || state[index - 1].active;
        const show = previousActive || active;

        this.fillIfChanged(level.select, level.placeholder, show ? items : [], active);
        level.select.disabled = !show || items.length === 0;
        this.setLoading(level.select, false);
      });
    }

    watchForm() {
      // Hidden inputs changed by something other than us (chips, drawer, sidebar)
      this.form.addEventListener('change', (event) => {
        if (this.selfWrite || this.contains(event.target)) return;
        const filter = event.target.getAttribute && event.target.getAttribute('data-filter');
        if (filter && this.levels.some((l) => filter.startsWith(`${l.param}=`))) this.syncFromForm();
      });

      // The theme re-rendering the hidden inputs
      let timer = null;
      new MutationObserver((mutations) => {
        if (!mutations.some((m) => !this.contains(m.target))) return;
        clearTimeout(timer);
        timer = setTimeout(() => this.syncFromForm(), 100);
      }).observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked'] });

      window.addEventListener('popstate', () => this.syncFromForm());
    }

    fillIfChanged(select, placeholder, items, selected) {
      const key = (v, l) => `${v}\u0000${l}`;
      const current = [...select.options].slice(1).map((o) => key(o.value, o.textContent)).join('\n');
      const next = items.map((i) => key(i.value, i.label)).join('\n');
      if (current !== next) this.fillItems(select, placeholder, items);
      if (select.value !== selected) select.value = selected;
    }

    fillItems(select, placeholder, items) {
      select.innerHTML = '';
      const add = (value, text, disabled = false) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = text;
        if (disabled) option.disabled = true;
        select.appendChild(option);
      };
      add('', placeholder);

      const { popular, other } = this.orderItems(items, select.dataset.popular);
      if (popular.length && other.length) add('', '-- Popular --', true);
      popular.forEach((item) => add(item.value, item.label || item.value));
      if (popular.length && other.length) add('', '-- Other --', true);
      other.forEach((item) => add(item.value, item.label || item.value));
    }

    /* Split items into the configured "popular" ones (kept in the configured
       order, only if they exist) and the rest (alphabetical). */
    orderItems(items, popularCsv) {
      const wanted = (popularCsv || '')
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      if (!wanted.length) return { popular: [], other: items };

      const matches = (item, name) =>
        item.value.toLowerCase() === name || (item.label || '').toLowerCase() === name;

      const popular = [];
      wanted.forEach((name) => {
        const hit = items.find((item) => matches(item, name) && !popular.includes(item));
        if (hit) popular.push(hit);
      });
      const other = items.filter((item) => !popular.includes(item));
      return { popular, other };
    }

    setLoading(select, isLoading) {
      select.toggleAttribute('data-loading', isLoading);
      select.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
  }

  customElements.define('tyre-finder', TyreFinder);
}
